/**
 * The machinery the academic-correspondence generators share: turning a form into a
 * letter made of the writer's own words and a bank of honest sentence frames.
 *
 * ## Marks
 *
 * A draft is a list of paragraphs, and a paragraph is a string that may carry three
 * kinds of mark, written as private-use characters so they can never collide with
 * anything a person types (input is scrubbed of them by `cleanLine`/`cleanBlock`):
 *
 * - a **placeholder** — `[Assignment]` — for a field the writer left blank, drawn
 *   highlighted on screen, kept in brackets in plain text, `<mark>`ed in rich text;
 * - **emphasis** — a journal's name, italic in rich text;
 * - **strong** — the "Comment 1.2" and "Response" labels of a response letter.
 *
 * Marking placeholders at assembly time, rather than finding `[…]` afterwards, is what
 * lets a reviewer's own "[sic]" or "[citation needed]" pass through as text.
 *
 * ## Frames
 *
 * A bank is a list of sentence frames. A frame may start with conditions —
 * `?progress:So far, {progress}.` is used only when `progress` is set, and
 * `!grade:I am writing about {assignment}.` only when `grade` is not — and `¶` on its
 * own starts a new paragraph. Values fill `{placeholders}` exactly as `t` fills every
 * other widget string, so a translation can override a bank through `ui` like any
 * other key.
 */

import { t } from "@/lib/i18n/interpolate";

const PH_OPEN = "";
const PH_CLOSE = "";
const EM_OPEN = "";
const EM_CLOSE = "";
const STRONG_OPEN = "";
const STRONG_CLOSE = "";

const MARKS = /[-]/g;
const PH_PAIR = /([^]*)/g;
const LINE_BREAKS = /\r\n?/g;
const SPACES = /[ \t ]+/g;
const ANY_WHITESPACE = /\s+/g;
const MANY_BLANK_LINES = /\n{3,}/g;
const SPACE_BEFORE_PUNCTUATION = / +([,.;:!?])/g;
const DOUBLE_PERIOD = /([^.])\.\.(?!\.)/g;
const QUESTION_PERIOD = /([?!])\./g;
const TRAILING_FRAGMENT_PUNCTUATION = /[\s.,;:。、，；：]+$/;
/** Latin, CJK full-width, Arabic and Devanagari sentence ends, then any closing mark. */
const SENTENCE_END = /[.!?…。！？؟।]["'”’)\]」』）]*$/;
const CONDITION = /^((?:[?!][A-Za-z0-9]+)+):/;
const CONDITION_PART = /([?!])([A-Za-z0-9]+)/g;
const LEADING_LETTER =
  /^([\s"'“‘([\uE000-\uE005]*)(\p{Ll}[\p{Ll}'’-]*)(?=[^\p{L}]|$)/u;
const HONORIFIC =
  /^(?:prof(?:essor)?|dr|mr|mrs|ms|miss|mx|herr|frau|sr|sra|dott(?:\.ssa)?)\.?\s+/i;
const TRAILING_STOP = /\.+$/;
const RANGE_DASH = /\s*(?:-|–|—|\bto\b)\s*/gi;
const LIST_SEPARATOR = /[,;\n]/;
const LINE_SEPARATOR = /[;\n]/;
const HTML_SPECIAL = /[&<>"']/g;
const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/** A paragraph break in a bank of frames. */
export const PARAGRAPH = "¶";

/* ------------------------------------------------------------------ marks */

export function placeholder(label: string): string {
  return `${PH_OPEN}${label}${PH_CLOSE}`;
}

export function emphasis(text: string): string {
  return text === "" ? "" : `${EM_OPEN}${text}${EM_CLOSE}`;
}

export function strong(text: string): string {
  return text === "" ? "" : `${STRONG_OPEN}${text}${STRONG_CLOSE}`;
}

export interface Segment {
  text: string;
  placeholder: boolean;
  italic: boolean;
  bold: boolean;
}

/** A paragraph as runs of text with their marks — what the on-page view draws. */
export function segmentsOf(paragraph: string): Segment[] {
  const runs: Segment[] = [];
  const state = { placeholder: false, italic: false, bold: false };
  let buffer = "";
  const flush = () => {
    if (buffer !== "") {
      runs.push({ text: buffer, ...state });
      buffer = "";
    }
  };
  for (const char of paragraph) {
    const toggle = MARK_TOGGLES[char];
    if (toggle) {
      flush();
      state[toggle.key] = toggle.on;
      continue;
    }
    buffer += char;
  }
  flush();
  return runs;
}

const MARK_TOGGLES: Record<
  string,
  { key: "placeholder" | "italic" | "bold"; on: boolean }
> = {
  [PH_OPEN]: { key: "placeholder", on: true },
  [PH_CLOSE]: { key: "placeholder", on: false },
  [EM_OPEN]: { key: "italic", on: true },
  [EM_CLOSE]: { key: "italic", on: false },
  [STRONG_OPEN]: { key: "bold", on: true },
  [STRONG_CLOSE]: { key: "bold", on: false },
};

/** Plain text: placeholders in square brackets, every other mark dropped. */
export function toPlain(paragraph: string): string {
  return paragraph.replace(PH_PAIR, "[$1]").replace(MARKS, "");
}

/** The whole draft as plain text, paragraphs separated by a blank line. */
export function draftToPlain(paragraphs: readonly string[]): string {
  return paragraphs.map(toPlain).join("\n\n");
}

function escapeHtml(text: string): string {
  return text.replace(HTML_SPECIAL, (char) => HTML_ESCAPES[char] ?? char);
}

function segmentToHtml(segment: Segment): string {
  let html = escapeHtml(segment.text).replaceAll("\n", "<br>");
  if (segment.placeholder) {
    html = `<mark>[${html}]</mark>`;
  }
  if (segment.italic) {
    html = `<i>${html}</i>`;
  }
  if (segment.bold) {
    html = `<b>${html}</b>`;
  }
  return html;
}

/** Rich text for the clipboard: one `<p>` per paragraph, marks as `<mark>`/`<i>`/`<b>`. */
export function draftToHtml(paragraphs: readonly string[]): string {
  return paragraphs
    .map(
      (paragraph) =>
        `<p>${segmentsOf(paragraph).map(segmentToHtml).join("")}</p>`
    )
    .join("");
}

/** How many blanks the writer still has to fill. */
export function countPlaceholders(paragraphs: readonly string[]): number {
  let count = 0;
  for (const paragraph of paragraphs) {
    for (const char of paragraph) {
      if (char === PH_OPEN) {
        count += 1;
      }
    }
  }
  return count;
}

/** Words as a reader counts them: runs of non-space, placeholders included. */
export function countWords(text: string): number {
  const plain = toPlain(text).trim();
  return plain === "" ? 0 : plain.split(ANY_WHITESPACE).length;
}

/* ----------------------------------------------------------------- input */

/** One line of input: marks scrubbed, whitespace collapsed, trimmed. */
export function cleanLine(raw: string | undefined): string {
  return (raw ?? "").replace(MARKS, "").replace(ANY_WHITESPACE, " ").trim();
}

/** Several lines of input: line breaks kept, at most one blank line in a row. */
export function cleanBlock(raw: string | undefined): string {
  return (raw ?? "")
    .replace(MARKS, "")
    .replace(LINE_BREAKS, "\n")
    .split("\n")
    .map((line) => line.replace(SPACES, " ").trim())
    .join("\n")
    .replace(MANY_BLANK_LINES, "\n\n")
    .trim();
}

/** Input that sits inside a frame's sentence: no closing full stop of its own. */
export function asFragment(raw: string | undefined): string {
  return cleanLine(raw).replace(TRAILING_FRAGMENT_PUNCTUATION, "");
}

/** Input that stands as one or more sentences: ends with terminal punctuation. */
export function asSentence(raw: string | undefined): string {
  const text = cleanLine(raw);
  if (text === "" || SENTENCE_END.test(text)) {
    return text;
  }
  return `${text}.`;
}

/** Items typed one per line (or separated by semicolons; commas too when `commas`). */
export function splitItems(
  raw: string | undefined,
  { commas = false }: { commas?: boolean } = {}
): string[] {
  return (raw ?? "")
    .split(commas ? LIST_SEPARATOR : LINE_SEPARATOR)
    .map((item) => asFragment(item))
    .filter((item) => item !== "");
}

/**
 * "A, B, and C" in the reader's language.
 *
 * When an item carries its own comma — "my parents, Rosa and Daniel" — the list is
 * punctuated so it still reads as the list it is: the separators of a longer list
 * become semicolons ("A, a; B; and C, c"), and in a pair the comma that closes the
 * first item's appositive is kept ("my parents, Rosa and Daniel, and my partner, Sam").
 */
export function joinList(items: readonly string[], locale: string): string {
  if (items.length === 0) {
    return "";
  }
  let format: Intl.ListFormat;
  try {
    format = new Intl.ListFormat(locale, { type: "conjunction" });
  } catch {
    format = new Intl.ListFormat("en", { type: "conjunction" });
  }
  const parts = format.formatToParts([...items]);
  if (!items.some((item) => item.includes(","))) {
    return parts.map((part) => part.value).join("");
  }
  let previous = "";
  return parts
    .map((part) => {
      if (part.type === "element") {
        previous = part.value;
        return part.value;
      }
      if (part.value.includes(",")) {
        return part.value.replaceAll(",", ";");
      }
      return previous.includes(",") ? `,${part.value}` : part.value;
    })
    .join("");
}

/**
 * Does a list end inside an appositive ("…and my partner, Sam")? Then the sentence
 * needs a comma after it before it carries on — see `compose`'s `open` keys.
 */
export function endsInAppositive(items: readonly string[]): boolean {
  return items.at(-1)?.includes(",") ?? false;
}

/* -------------------------------------------------------------- composing */

/** A value for a frame: the writer's text, or a marked placeholder when blank. */
export function orPlaceholder(value: string, label: string): string {
  return value === "" ? placeholder(label) : value;
}

/** Capitalise a sentence that opens with an all-lowercase word ("the lab…"), not "eLife". */
export function capitalizeFirst(text: string): string {
  const match = LEADING_LETTER.exec(text);
  if (!match) {
    return text;
  }
  const [, before, word] = match;
  return `${before}${word.charAt(0).toUpperCase()}${text.slice(before.length + 1)}`;
}

/** Close the seams optional values leave: doubled spaces, " ,", "..", "?.". */
export function tidy(text: string): string {
  return text
    .replace(SPACES, " ")
    .replace(SPACE_BEFORE_PUNCTUATION, "$1")
    .replace(DOUBLE_PERIOD, "$1.")
    .replace(QUESTION_PERIOD, "$1")
    .trim();
}

function passes(conditions: string, flags: ReadonlySet<string>): boolean {
  for (const [, sign, name] of conditions.matchAll(CONDITION_PART)) {
    const on = flags.has(name);
    if ((sign === "?" && !on) || (sign === "!" && on)) {
      return false;
    }
  }
  return true;
}

const PLACEHOLDER = /\{(\w+)\}/g;
const WORD_FOLLOWS = /^ [\p{L}\p{N}\uE000-\uE005]/u;

/**
 * `t`, plus one English courtesy: a value listed in `open` ends inside an appositive,
 * so when the sentence carries straight on after it — "I thank my partner, Sam for…" —
 * the closing comma is added: "…my partner, Sam, for…".
 */
function interpolate(
  template: string,
  values: Readonly<Record<string, string>>,
  open: ReadonlySet<string>
): string {
  return template.replace(PLACEHOLDER, (match, key: string, offset: number) => {
    const value = values[key];
    if (value === undefined) {
      return match;
    }
    const after = template.slice(offset + match.length);
    return open.has(key) && WORD_FOLLOWS.test(after) ? `${value},` : value;
  });
}

const NONE: ReadonlySet<string> = new Set();

/**
 * One frame, filled: `null` when its conditions exclude it.
 *
 * Exported for single-line frames (subject lines, alternatives) that are chosen from a
 * list rather than composed into paragraphs.
 */
export function fillFrame(
  frame: string,
  values: Readonly<Record<string, string>>,
  flags: ReadonlySet<string>,
  open: ReadonlySet<string> = NONE
): string | null {
  const condition = CONDITION.exec(frame);
  let body = frame;
  if (condition) {
    if (!passes(condition[1], flags)) {
      return null;
    }
    body = frame.slice(condition[0].length);
  }
  const filled = capitalizeFirst(tidy(interpolate(body, values, open)));
  return filled === "" ? null : filled;
}

/** Every frame that applies, in order. */
export function fillFrames(
  frames: readonly string[],
  values: Readonly<Record<string, string>>,
  flags: ReadonlySet<string>
): string[] {
  const out: string[] = [];
  for (const frame of frames) {
    const filled = fillFrame(frame, values, flags);
    if (filled !== null) {
      out.push(filled);
    }
  }
  return out;
}

/**
 * A bank of frames as paragraphs: sentences joined by spaces, `¶` between paragraphs.
 * `open` names the values that end inside an appositive (see `interpolate`).
 */
export function compose(
  frames: readonly string[],
  values: Readonly<Record<string, string>>,
  flags: ReadonlySet<string>,
  open: ReadonlySet<string> = NONE
): string[] {
  const paragraphs: string[] = [];
  let sentences: string[] = [];
  const flush = () => {
    if (sentences.length > 0) {
      paragraphs.push(sentences.join(" "));
      sentences = [];
    }
  };
  for (const frame of frames) {
    if (frame === PARAGRAPH) {
      flush();
      continue;
    }
    const filled = fillFrame(frame, values, flags, open);
    if (filled !== null) {
      sentences.push(filled);
    }
  }
  flush();
  return paragraphs;
}

/* ------------------------------------------------------- names and titles */

/**
 * Scripts that are written without spaces. A title in one of them can sit directly
 * against the name ("田中教授", "教授田中"), and can follow it ("김 교수님").
 */
const UNSPACED_SCRIPT =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/u;

/** Honorifics that follow a Japanese or Chinese name, whatever the locale's title list says. */
const SUFFIX_HONORIFICS = ["先生", "様"] as const;

function startsWithIgnoringCase(text: string, prefix: string): boolean {
  return text.slice(0, prefix.length).toLowerCase() === prefix.toLowerCase();
}

function endsWithIgnoringCase(text: string, suffix: string): boolean {
  return (
    text.length > suffix.length &&
    text.slice(text.length - suffix.length).toLowerCase() ===
      suffix.toLowerCase()
  );
}

/**
 * The name without a leading `title`, or null when it does not start with one. The
 * title must end at a word boundary — a space, or a full stop after it — unless it is
 * written in a script that does not use spaces, so "Drake" and "Professorova" survive.
 */
function withoutPrefixTitle(name: string, title: string): string | null {
  if (!startsWithIgnoringCase(name, title)) {
    return null;
  }
  let rest = name.slice(title.length);
  const hadStop = rest.startsWith(".");
  if (hadStop) {
    rest = rest.slice(1);
  }
  const trimmed = rest.trimStart();
  const bounded =
    hadStop || trimmed.length < rest.length || UNSPACED_SCRIPT.test(title);
  return bounded && trimmed !== "" ? trimmed : null;
}

/** The name without a trailing `title` ("田中先生"), for titles in unspaced scripts only. */
function withoutSuffixTitle(name: string, title: string): string | null {
  if (!(UNSPACED_SCRIPT.test(title) && endsWithIgnoringCase(name, title))) {
    return null;
  }
  const rest = name.slice(0, name.length - title.length).trimEnd();
  return rest === "" ? null : rest;
}

/**
 * The name a person is addressed by, with any title the writer typed into the name box
 * removed — "Dr. Okafor" with "Professor" chosen is "Professor Okafor", not
 * "Professor Dr. Okafor".
 *
 * English titles are always known. `extraTitles` adds the current locale's own words
 * ("доктор", "博士", "Frau"), matched case-insensitively at the start of the name with
 * an optional full stop, and, for Chinese, Japanese and Korean titles, at its end.
 */
export function stripHonorific(
  name: string,
  extraTitles: readonly string[] = []
): string {
  const known =
    extraTitles.length > 0 ? [...extraTitles, ...SUFFIX_HONORIFICS] : [];
  const titles = known
    .map((title) => title.trim().replace(TRAILING_STOP, ""))
    .filter((title) => title !== "");
  let rest = cleanLine(name);
  let previous = "";
  while (rest !== previous) {
    previous = rest;
    rest = rest.replace(HONORIFIC, "");
    for (const title of titles) {
      rest =
        withoutPrefixTitle(rest, title) ??
        withoutSuffixTitle(rest, title) ??
        rest;
    }
  }
  return rest;
}

/**
 * "Professor Okafor"; just the name when there is no title word. A locale that puts the
 * title after the name ("Okafor 교수님") passes `format` as "{name} {title}"; without
 * one the title comes first.
 */
export function addressName(
  title: string,
  name: string,
  format?: string
): string {
  if (title === "") {
    return name;
  }
  return format ? t(format, { title, name }) : `${title} ${name}`;
}

/**
 * A line reference as a manuscript writes it: "112-118" becomes "112–118", and one
 * line is "line", more than one "lines".
 */
export function normalizeLines(raw: string | undefined): {
  text: string;
  plural: boolean;
} {
  const text = cleanLine(raw).replace(RANGE_DASH, "–");
  return { text, plural: text.includes("–") || text.includes(",") };
}

/* -------------------------------------------------------- English articles */

/*
 * "a"/"an" and "the University of …" are English grammar, so these two helpers are
 * English-only by design; the tools that use them publish in English first.
 */

const HAS_ARTICLE = /^(?:a|an|the)\s/i;
/** Unique roles take no article: "is Director of the Reef Lab". */
const UNIQUE_ROLE =
  /^(?:head|director|dean|chair|provost|president|principal|rector|chancellor|vice-chancellor|pro-vice-chancellor|deputy)\b/i;
/** Spoken letter by letter: "an MSc student", "an NIHR fellow", "a PhD candidate". */
const INITIALISM = /^(?:[A-Z][A-Za-z]?[A-Z]|[A-Z]{2})/;
/** Initialisms read as words: "a NASA engineer". */
const WORD_ACRONYM = /^(?:NASA|NATO|NOAA|FIFA|SAGE|LEGO)\b/;
/** Letters whose names start with a vowel sound: A, E, F, H, I, L, M, N, O, R, S, X. */
const VOWEL_LETTER_NAME = /^[AEFHILMNORSX]/;
const VOWEL = /^[aeiou]/i;
/** Vowel letters that sound like a consonant: "a university", "a European", "a one-year". */
const CONSONANT_SOUND = /^(?:uni|use|usu|uti|ura|uro|ubi|eu|one\b|once\b)/i;
/** A silent h: "an honorary fellow", "an hour". */
const SILENT_H = /^(?:hour|honou?r|honest|heir)/i;
const NEEDS_THE = /^(?:University|Institute|College|School|Academy) of\b/;

function takesAn(phrase: string): boolean {
  if (INITIALISM.test(phrase) && !WORD_ACRONYM.test(phrase)) {
    return VOWEL_LETTER_NAME.test(phrase);
  }
  if (SILENT_H.test(phrase)) {
    return true;
  }
  return VOWEL.test(phrase) && !CONSONANT_SOUND.test(phrase);
}

/**
 * "an associate professor", "a PhD candidate", "an MSc student" — and no article for a
 * phrase that already has one or names a unique role ("Director of the Reef Lab").
 */
export function withIndefiniteArticle(phrase: string): string {
  if (phrase === "" || HAS_ARTICLE.test(phrase) || UNIQUE_ROLE.test(phrase)) {
    return phrase;
  }
  return `${takesAn(phrase) ? "an" : "a"} ${phrase}`;
}

/** "University of Lagos" reads "the University of Lagos" in a sentence. */
export function withDefiniteArticle(name: string): string {
  return NEEDS_THE.test(name) ? `the ${name}` : name;
}
